import { useState } from "react";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { type components } from "@jbnl/api-client";
import { FormActions } from "@jbnl/shared-ui";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useIssueLokaalAccount } from "./useLokaleAccounts";

type Kandidaat = components["schemas"]["PersoonMatch"];

/**
 * Stays open after submitting, unlike the modals it copies: those end in a record, this one ends
 * in an answer - and closing would throw away the question about an ambiguous address.
 */
export function IssueLokaalAccount() {
  const { t } = useTranslation(["lokaleAccounts", "common"]);
  const issue = useIssueLokaalAccount();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");

  // Closing throws the half-typed address away rather than keeping it for next time: reopening
  // to last week's abandoned draft, or to last week's answer, is the surprise - not an empty
  // form.
  function wissel(volgende: boolean) {
    setOpen(volgende);
    if (!volgende) {
      setEmail("");
      issue.reset();
    }
  }

  return (
    <Dialog open={open} onOpenChange={wissel}>
      <DialogTrigger asChild>
        <Button>
          <Plus />
          {t("uitnodigen.title")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("uitnodigen.title")}</DialogTitle>
          <DialogDescription>{t("uitnodigen.uitleg")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <form
            className="space-y-2"
            onSubmit={(event) => {
              event.preventDefault();
              issue.mutate({ email });
            }}
          >
            <div className="grid gap-2">
              <Label htmlFor="uitnodiging-email">{t("uitnodigen.email")}</Label>
              <Input
                id="uitnodiging-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
              />
            </div>
            <FormActions>
              <Button type="submit" disabled={issue.isPending}>
                {issue.isPending
                  ? t("common:actions.busy")
                  : t("uitnodigen.submit")}
              </Button>
            </FormActions>
          </form>

          <UitnodigingResultaat issue={issue} email={email} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** The four things an invite attempt can lead to: an error, an automatic or manual link, or a choice. */
function UitnodigingResultaat({
  issue,
  email,
}: {
  issue: ReturnType<typeof useIssueLokaalAccount>;
  email: string;
}) {
  const { t } = useTranslation(["lokaleAccounts", "common"]);
  const candidates =
    issue.data?.status === "kies-persoon" ? (issue.data.candidates ?? []) : [];

  return (
    <>
      {issue.error && (
        <p className="text-sm text-destructive">{issue.error.message}</p>
      )}

      {issue.data?.status === "automatisch-gekoppeld" && (
        <p className="text-sm">{t("uitnodigen.automatischGekoppeld")}</p>
      )}

      {issue.data?.status === "gekoppeld" && (
        <p className="text-sm">{t("uitnodigen.verstuurd")}</p>
      )}

      {issue.data?.status === "kies-persoon" && (
        <KandidaatKeuze
          candidates={candidates}
          onKies={(persoonId) => issue.mutate({ email, persoonId })}
        />
      )}
    </>
  );
}

/** Candidates, never a proposal: a pre-selected row is the wrong click already made (ADR-0018). */
function KandidaatKeuze({
  candidates,
  onKies,
}: {
  candidates: Kandidaat[];
  onKies: (persoonId: string) => void;
}) {
  const { t } = useTranslation(["lokaleAccounts", "common"]);

  return (
    <div className="space-y-2 rounded-md border p-3" data-testid="kies-persoon">
      {candidates.length === 0 ? (
        <p className="text-sm">{t("uitnodigen.geenKandidaten")}</p>
      ) : (
        <>
          <p className="text-sm">{t("uitnodigen.meerdereKandidaten")}</p>
          <ul className="space-y-1">
            {candidates.map((candidate) => (
              <li
                key={candidate.persoonId}
                className="flex items-center justify-between gap-2 text-sm"
              >
                <span>
                  {candidate.email
                    ? t("uitnodigen.kandidaat", {
                        naam: candidate.naam,
                        email: candidate.email,
                      })
                    : candidate.naam}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onKies(candidate.persoonId)}
                >
                  {t("uitnodigen.kies")}
                </Button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
